import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import BusinessIcon from '@mui/icons-material/Business';
import FavoriteIcon from '@mui/icons-material/Favorite';

import corporateImg from '../../assets/photos/sushi3.jpg';
import weddingImg from '../../assets/photos/comida5.webp';

const EVENT_TYPES = [
  {
    icon: <BusinessIcon sx={{ fontSize: 32 }} />,
    label: 'Eventos corporativos',
    title: 'Sushi para empresas que marca la diferencia',
    desc: 'Reuniones de equipo, lanzamientos de producto, inauguraciones, afterworks… Diseñamos el buffet de sushi corporativo que mejor encaje con tu marca, presupuesto y número de invitados.',
    tags: ['Team building', 'Lanzamientos', 'Afterworks', 'Inauguraciones'],
    image: corporateImg,
  },
  {
    icon: <FavoriteIcon sx={{ fontSize: 32 }} />,
    label: 'Bodas',
    title: 'Catering de sushi que hará tu boda inolvidable',
    desc: 'Combinamos piezas clásicas y creaciones innovadoras para que todos los invitados encuentren su bocado perfecto. Una experiencia gastronómica única en el día más especial.',
    tags: ['Ceremonias', 'Cócteles', 'Banquetes', 'Celebraciones privadas'],
    image: weddingImg,
  },
];

const panelVariants = {
  hidden: (i: number) => ({ opacity: 0, x: i === 0 ? -50 : 50 }),
  visible: { opacity: 1, x: 0, transition: { duration: 0.75, ease: 'easeOut' as const } },
};

interface Props {
  onOpenContact: () => void;
}

const Events: React.FC<Props> = ({ onOpenContact }) => {
  return (
    <Box id="events" sx={{ py: 10, background: 'var(--color-bg-section)' }}>
      {/* Section header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Box sx={{ textAlign: 'center', mb: 8, px: 2 }}>
          <Typography
            variant="overline"
            sx={{ display: 'block', color: 'var(--color-accent)', letterSpacing: '0.3em', mb: 1 }}
          >
            Especialidades
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'var(--font-heading)',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              color: 'var(--color-text-primary)',
            }}
          >
            Para cada celebración, una experiencia a medida
          </Typography>
        </Box>
      </motion.div>

      {/* Split panels */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          mx: { xs: 2, sm: 4, md: 8 },
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid var(--color-border)',
          minHeight: { xs: 'auto', md: 580 },
        }}
      >
        {EVENT_TYPES.map((ev, i) => (
          <motion.div
            key={i}
            custom={i}
            variants={panelVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            style={{ flex: 1, display: 'flex', minHeight: 0 }}
          >
            <Box
              sx={{
                flex: 1,
                position: 'relative',
                minHeight: { xs: 460, md: 580 },
                backgroundImage: `url(${ev.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'flex-end',
                borderRight: i === 0 ? { xs: 'none', md: '1px solid var(--color-border)' } : 'none',
                borderBottom: i === 0 ? { xs: '1px solid var(--color-border)', md: 'none' } : 'none',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(to top, rgba(5,4,2,0.96) 0%, rgba(5,4,2,0.6) 45%, rgba(5,4,2,0.1) 100%)',
                  transition: 'opacity 0.4s',
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  p: { xs: 4, md: 5 },
                  width: '100%',
                }}
              >
                <Box sx={{ color: 'var(--color-accent)', mb: 2 }}>{ev.icon}</Box>

                <Typography
                  variant="overline"
                  sx={{
                    color: 'var(--color-accent)',
                    letterSpacing: '0.25em',
                    fontSize: '0.7rem',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {ev.label}
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'var(--font-heading)',
                    color: 'var(--color-text-primary)',
                    fontSize: { xs: '1.35rem', md: '1.55rem' },
                    lineHeight: 1.3,
                    mb: 2,
                  }}
                >
                  {ev.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(240,236,228,0.75)',
                    lineHeight: 1.85,
                    mb: 3,
                    maxWidth: 460,
                  }}
                >
                  {ev.desc}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3.5 }}>
                  {ev.tags.map((tag) => (
                    <Box
                      key={tag}
                      sx={{
                        px: 1.5,
                        py: 0.4,
                        border: '1px solid rgba(200,169,110,0.45)',
                        borderRadius: 1,
                        fontSize: '0.72rem',
                        color: 'var(--color-accent)',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {tag}
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={onOpenContact}
                  sx={{
                    color: 'var(--color-accent)',
                    borderColor: 'rgba(200,169,110,0.6)',
                    letterSpacing: '0.12em',
                    fontSize: '0.78rem',
                    px: 3,
                    py: 1,
                    '&:hover': {
                      bgcolor: 'var(--color-accent)',
                      borderColor: 'var(--color-accent)',
                      color: '#0d0d0d',
                    },
                  }}
                >
                  Pedir presupuesto
                </Button>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Box>
  );
};

export default Events;
